// --- Dependencies -------------------------------------------------------------------------------
import { useMemo, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useResourceTable from "@/hooks/useResourceTable";
import useTranslation from "@/hooks/useTranslation";
import type { TySubscriptionPeriod, TySubscriptionRow, TySubscriptionStatus } from "@/types";
import StatusBadge from "@/UI/atoms/StatusBadge";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import DataTable from "@/UI/components/DataTable";
import type { TyColumn } from "@/UI/components/DataTable/types";
import StatsSection from "@/UI/components/StatsSection";
import ListScreen from "@/UI/components/ListScreen";
import FilterSelect from "@/UI/inputs/FilterSelect";
import ActivatePopup from "@/UI/popups/ActivatePopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { formatDate, formatMoney, formatNumber } from "@/utils/format";

// --- Types --------------------------------------------------------------------------------------

type TyStats = { total: number; active: number; pending: number; revenue: number };

// --- Constants ----------------------------------------------------------------------------------

const STATUSES: TySubscriptionStatus[] = ["pending", "active", "coming", "grace", "paused", "canceled", "ended"];

/**
 * What the inline cell may set — everything but "active".
 *
 * A cell writes one field, and the API refuses "active" without an end date, because a live plan with
 * no end never expires and reads "0 days left" to the client. Starting a plan goes through the
 * activate action, which works the dates out from the payment; the bulk bar can still force it, since
 * it can send the end date in the same breath.
 */
const CELL_STATUSES = STATUSES.filter((one) => one !== "active");

const PERIODS: TySubscriptionPeriod[] = ["monthly", "quarterly", "annually"];

// --- Logic --------------------------------------------------------------------------------------

export default function SubscriptionsScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { canEdit } = usePermission();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState("");
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [period, setPeriod] = useState("");

    /** The three that need a click: everything, the queue waiting on money, and what is live. */
    const tabStatus = tab === "pending" ? "pending" : tab === "active" ? "active" : "";

    const table = useResourceTable<TySubscriptionRow, TyStats>({
        path: "subscriptions",
        rowsKey: "subscriptions",
        query: { search, period, status: status || tabStatus },
    });

    const isEditable = canEdit("subscriptions");
    const money = (value: number) => formatMoney(value, lang, t.home.egp);

    const stats = useMemo(() => {
        if (!table.stats) return [];
        return [
            { key: "total", label: t.lists.subscriptions.statTotal, value: formatNumber(table.stats.total, lang), tone: "brand" as const },
            { key: "active", label: t.lists.subscriptions.statActive, value: formatNumber(table.stats.active, lang), tone: "good" as const },
            { key: "pending", label: t.lists.subscriptions.statPending, value: formatNumber(table.stats.pending, lang), tone: "warn" as const },
            { key: "revenue", label: t.lists.subscriptions.statRevenue, value: money(table.stats.revenue), tone: "brand" as const },
        ];
    }, [table.stats, t, lang]);

    const columns = useMemo<TyColumn<TySubscriptionRow>[]>(
        () => [
            {
                key: "store",
                label: t.cols.store,
                width: "minmax(170px, 1.4fr)",
                render: (row) => <span className="list-cell-strong">{row.store?.name ?? "—"}</span>,
            },
            {
                key: "status",
                label: t.cols.status,
                width: "150px",
                render: (row) => <StatusBadge status={row.status} />,
                /* A plain write, deliberately not a substitute for `activate` — see the controller. */
                edit: isEditable
                    ? {
                          kind: "select",
                          options: CELL_STATUSES.map((one) => ({ value: one, label: t.enums.subscriptionStatus[one] })),
                          value: (row) => row.status,
                      }
                    : undefined,
            },
            {
                key: "period",
                label: t.cols.period,
                width: "130px",
                render: (row) => <span>{t.enums.subscriptionPeriod[row.period]}</span>,
            },
            {
                key: "price",
                label: t.cols.price,
                width: "110px",
                render: (row) => <span className="list-cell-strong">{money(row.price)}</span>,
            },
            {
                key: "couponDiscount",
                label: t.cols.discount,
                width: "110px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{row.couponDiscount ? money(row.couponDiscount) : "—"}</span>,
            },
            {
                key: "endDate",
                label: t.cols.endDate,
                width: "130px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDate(row.endDate, lang)}</span>,
                edit: isEditable ? { kind: "date", nullable: true, value: (row) => row.endDate } : undefined,
            },
            {
                key: "createdAt",
                label: t.cols.created,
                width: "130px",
                secondary: true,
                render: (row) => <span className="list-cell-muted">{formatDate(row.createdAt, lang)}</span>,
            },
        ],
        [t, lang, isEditable]
    );

    /**
     * The only path that actually starts a plan. The inline status cell can write "active" too, but it
     * is a plain field write — it sets no dates, which leaves a live subscription reading "0 days left"
     * and expiring never. Activation has to come through here.
     */
    const confirmActivate = (row: TySubscriptionRow) => {
        removeComp("activate-sub");
        addComp({
            name: "activate-sub",
            Component: ActivatePopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                expectedAmount: row.price,
                storeName: row.store?.name ?? "—",
                /* A store already holding a live plan gets this one queued behind it rather than
                   started today. Read off the rows in hand, so it is a heads-up and not a promise —
                   the API decides, and an active row on another page would not be seen from here. */
                willQueue: table.rows.some((other) => other.status === "active" && other.store?._id === row.store?._id),
                onConfirm: (amountPaid: number) => table.runAction(row._id, "activate", { amountPaid }),
            },
        });
    };

    const confirmDelete = (ids: string[], label: string) => {
        removeComp("confirm-delete-sub");
        addComp({
            name: "confirm-delete-sub",
            Component: ConfirmPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details: {
                message: `${t.lists.subscriptions.deleteWarning} — ${label}`,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                confirmLabel: t.table.deleteConfirm,
                cancelLabel: t.table.deleteCancel,
                onConfirm: () => table.deleteBulk(ids),
            },
        });
    };

    return (
        <ListScreen writeError={table.writeError}>
            <StatsSection loading={table.loading && !table.stats} stats={stats} />

            <ActionsBar
                tabs={[
                    { value: "", label: t.lists.subscriptions.tabAll },
                    { value: "pending", label: t.lists.subscriptions.tabPending },
                    { value: "active", label: t.lists.subscriptions.tabActive },
                ]}
                tab={tab}
                onTab={setTab}
                search={{ value: search, onChange: setSearch, placeholder: t.lists.subscriptions.search }}
                activeFilterCount={[status, period].filter(Boolean).length}
                onClearFilters={() => {
                    setStatus("");
                    setPeriod("");
                }}
                filters={
                    <>
                        <FilterSelect
                            label={t.cols.status}
                            value={status}
                            onChange={setStatus}
                            options={STATUSES.map((one) => ({ value: one, label: t.enums.subscriptionStatus[one] }))}
                        />
                        <FilterSelect
                            label={t.cols.period}
                            value={period}
                            onChange={setPeriod}
                            options={PERIODS.map((one) => ({ value: one, label: t.enums.subscriptionPeriod[one] }))}
                        />
                    </>
                }
            />

            <DataTable<TySubscriptionRow>
                rows={table.rows}
                columns={columns}
                rowId={(row) => row._id}
                loading={table.loading}
                error={table.error}
                onRetry={table.refetch}
                pager={{ page: table.page, lastPage: table.lastPage, total: table.total, onPage: table.setPage }}
                selection={isEditable ? { selected: table.selected, onChange: table.setSelected } : undefined}
                onCellSave={isEditable ? (row, key, value) => table.saveCell(row._id, key, value) : undefined}
                rowActions={(row) =>
                    isEditable ? (
                        <>
                            {/* Only a row awaiting payment can be activated; the rest have nothing to confirm. */}
                            {row.status === "pending" && (
                                <button
                                    type="button"
                                    className="list-row-btn list-row-btn--brand"
                                    onClick={() => confirmActivate(row)}
                                    aria-label={t.activatePopup.title}
                                >
                                    <img src="/icons/check-icon.svg" alt="" width={15} height={15} />
                                </button>
                            )}
                            <button
                                type="button"
                                className="list-row-btn list-row-btn--danger"
                                onClick={() => confirmDelete([row._id], row.store?.name ?? row._id)}
                                aria-label={t.table.deleteRow}
                            >
                                <img src="/icons/delete-icon.svg" alt="" width={15} height={15} />
                            </button>
                        </>
                    ) : null
                }
            />

            {isEditable && (
                <BulkBar
                    count={table.selected.length}
                    busy={table.busy}
                    fields={[
                        {
                            key: "status",
                            label: t.cols.status,
                            kind: "select",
                            options: STATUSES.map((one) => ({ value: one, label: t.enums.subscriptionStatus[one] })),
                        },
                        { key: "endDate", label: t.cols.endDate, kind: "date" },
                    ]}
                    onApply={table.applyBulk}
                    onDelete={() => confirmDelete(table.selected, `${table.selected.length}`)}
                    onClear={() => table.setSelected([])}
                />
            )}
        </ListScreen>
    );
}
